import { Button } from "antd";
import { PlayIcon, PauseIcon } from "@phosphor-icons/react";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";
import { useMemo } from "react";

export interface PlayScriptProps {
  text: string;
  size?: "small" | "middle" | "large";
  disabled?: boolean;
  sectionId?: string;
  slideId?: string;
  /** Resolved voice for this section, e.g. from useSectionVoiceId. Lets a
   * per-section avatar/voice change bust stale cached audio. */
  voiceId?: string;
  style?: React.CSSProperties;
}

/**
 * PlayScript - Audio playback button for TTS scripts.
 * Plays the text that is provided in the text prop or the text of the section if sectionId is provided.
 *
 * @requires TTSProvider - This component must be used within a TTSProvider.
 * Wrap the parent storyboard component with <TTSProvider> to enable TTS functionality.
 *
 * When multiple PlayScript components exist on a page, clicking play on one
 * will automatically stop any other currently playing audio.
 *
 * @example
 * // In parent storyboard component:
 * <TTSProvider>
 *   <StoryboardContent>
 *     // For sections:
 *     <PlayScript sectionId={section.id} text={script} />
 *     // For slides:
 *     <PlayScript slideId={slide.uuid} text={script} />
 *   </StoryboardContent>
 * </TTSProvider>
 */

export const PlayScript = ({
  text,
  size = "middle",
  disabled = false,
  sectionId,
  slideId,
  voiceId,
  style,
}: PlayScriptProps) => {
  const {
    isPlaying,
    isPaused,
    isLoading,
    isConnected,
    play,
    pause,
    resume,
    wsBusy,
  } = useTextToSpeech({ sectionId, slideId, voiceId });

  const handleClick = () => {
    if (isPlaying) {
      pause().catch((error) => console.error("Error pausing audio", error));
    } else if (isPaused) {
      resume(text).catch((error) =>
        console.error("Error resuming audio", error)
      );
    } else {
      play(text);
    }
  };
  const isLoadingAudio = isLoading && !isPlaying && !isPaused;

  const canControlCurrentPlayback = isPlaying || isPaused;
  const isButtonDisabled =
    disabled ||
    !isConnected ||
    isLoadingAudio ||
    (wsBusy && !canControlCurrentPlayback);

  const icon = useMemo(() => {
    if (isPlaying) {
      return <PauseIcon weight="fill" />;
    }
    return <PlayIcon weight="fill" />;
  }, [isPlaying]);

  return (
    <Button
      shape="circle"
      type="primary"
      loading={isLoadingAudio}
      size={size}
      icon={icon}
      onClick={handleClick}
      disabled={isButtonDisabled}
      aria-label={isPlaying ? "Pause" : "Play"}
      style={style}
    />
  );
};
